import axios from 'axios'

import { API_BASE_URL, storage } from 'utils'

/**
 * Subdomain availability probe.
 *
 * `GET /companies/public/subdomain/check/:subdomain` has existed since the
 * endpoint was written but had NO caller anywhere in either frontend — meanwhile
 * the Installation page rendered a "Checking availability…" line that was wired
 * to the save mutation's `isSubmitting`, so it appeared for a moment while the
 * PATCH was in flight and checked nothing. The UI claimed a check it never made,
 * and a collision only surfaced as a failed save.
 *
 * The route is PUBLIC, so this uses a bare axios instance rather than the shared
 * authed `api` — same reasoning as signupApi: the shared instance's interceptors
 * bounce to /sign-in on 401 and would obscure a plain 400 from a malformed
 * subdomain.
 */
const subdomainClient = axios.create({ baseURL: API_BASE_URL })

export interface SubdomainAvailability {
  available: boolean
  subdomain: string
}

/**
 * Returns `available: false` for a taken OR reserved subdomain, and throws on a
 * malformed one (the endpoint answers 400).
 *
 * CALLER BEWARE: the endpoint resolves availability purely by "does any company
 * own this", so a company's OWN current subdomain comes back as unavailable.
 * Callers editing an existing value must exempt it — see the Installation page,
 * which short-circuits when the input still matches the saved subdomain.
 *
 * VERA-1095: while signup is gated, the endpoint requires proof of invitation —
 * either an authenticated admin session or a valid `invite` token — and answers
 * 403 otherwise. `inviteToken` carries the latter (the gated public /sign-up
 * page's `?invite=` param). The FORMER is attached automatically whenever an
 * admin token exists in storage — this is deliberately unconditional rather
 * than a caller-supplied flag: it's exactly the mechanism the shared `instance`
 * client uses (`X-Vera-Token`, see services/index.ts's `retryWithToken`), just
 * applied to this bare, interceptor-free client. It's what keeps the
 * Installation settings page's own subdomain check working under the gate
 * without that page needing to know anything about signup invites.
 */
export const checkSubdomainAvailability = async (
  subdomain: string,
  inviteToken?: string,
): Promise<SubdomainAvailability> => {
  const adminToken = storage.getItem('accessToken')
  const response = await subdomainClient.get<SubdomainAvailability>(
    `/companies/public/subdomain/check/${encodeURIComponent(subdomain)}`,
    {
      params: inviteToken ? { invite: inviteToken } : undefined,
      headers: adminToken ? { 'X-Vera-Token': adminToken } : undefined,
    },
  )
  return response.data
}
